import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { Group, Role } from "../interfaces/auth.interface";

type LoadingState = "IDLE" | "LOADING" | "DONE";

export interface AuthenticationState {
  loading: LoadingState;
  tableau_token: string | null;
  portal_token: string | null;
  error: string | null;
  loadingRoles: LoadingState;
  roleError: string | null;
  appRoles: Role[];
  appGroups: Group[];
  dashboardAccess: number | null;
  loadingDashboardAccess: LoadingState;
}

const initialState: AuthenticationState = {
  loading: "IDLE",
  tableau_token: null,
  portal_token: null,
  error: null,
  loadingRoles: "IDLE",
  roleError: null,
  appRoles: [],
  appGroups: [],
  dashboardAccess: null,
  loadingDashboardAccess: "IDLE",
};

export const fetchTokens = createAsyncThunk(
  "authentication/fetchTokens",
  async ({ email, token }: { email: string; token: string }) => {
    const response = await fetch(process.env.REACT_APP_API_ENDPOINT!, {
      method: "POST",
      body: JSON.stringify({ email }),
      headers: { Authorization: token },
    });
    return response.json();
  }
);

export const fetchRoles = createAsyncThunk(
  "authentication/fetchRoles",
  async ({ token }: { token: string }) => {
    const response = await fetch(
      `${process.env.REACT_APP_PFS_BASE_URL}/auth/roles`,
      {
        method: "GET",
        headers: { Authorization: token },
      }
    );
    return response.json();
  }
);

export const fetchDashboardAccess = createAsyncThunk(
  "authentication/fetchDashboardAccess",
  async ({ token }: { token: string }, { rejectWithValue }) => {
    const response = await fetch(
      `${process.env.REACT_APP_PFS_BASE_URL}/dashboard-access`,
      {
        method: "GET",
        headers: { Authorization: token },
      }
    );
    const data = await response.json();
    
    return data;
  }
);

const authenticationSlice = createSlice({
  name: "authentication",
  initialState,
  reducers: {
    clearAuthenticationState: (state) => {
      state.loading = "IDLE";
      state.tableau_token = null;
      state.portal_token = null;
      state.error = null;
      state.loadingRoles = "IDLE";
      state.roleError = null;
      state.appRoles = [];
      state.appGroups = [];
      state.dashboardAccess = null;
      state.loadingDashboardAccess = "IDLE";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTokens.pending, (state) => {
        state.loading = "LOADING";
        state.error = null;
        state.portal_token = null;
        state.tableau_token = null;
      })
      .addCase(fetchTokens.rejected, (state, action) => {
        state.loading = "DONE";
        state.error = action.error.message ?? null;
        state.portal_token = null;
        state.tableau_token = null;
      })
      .addCase(fetchTokens.fulfilled, (state, action) => {
        state.loading = "DONE";
        state.error = null;
        state.portal_token = action.payload.portal_token;
        state.tableau_token = action.payload.tableau_token;
      })
      .addCase(fetchRoles.pending, (state) => {
        state.loadingRoles = "LOADING";
        state.roleError = null;
        state.appRoles = [];
        state.appGroups = [];
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.loadingRoles = "DONE";
        state.roleError = action.error.message ?? null;
        state.appRoles = [];
        state.appGroups = [];
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.loadingRoles = "DONE";
        state.roleError = null;
        state.appGroups = action.payload.groups;
        state.appRoles = action.payload.roles;
      })
      .addCase(fetchDashboardAccess.pending, (state) => {
        state.loadingDashboardAccess = "LOADING";
      })
      .addCase(fetchDashboardAccess.rejected, (state) => {
        state.dashboardAccess = 0;
        state.loadingDashboardAccess = "DONE";
      })
      .addCase(fetchDashboardAccess.fulfilled, (state, action) => {
        state.dashboardAccess = action.payload.count;
        state.loadingDashboardAccess = "DONE";
      });
  },
});

export const { clearAuthenticationState } = authenticationSlice.actions;
export const { reducer } = authenticationSlice;
